import type { ReviewRow } from "@/types";

/** MIME type for HTML5 drag-and-drop of a review table row. */
export const REVIEW_SECTION_MIME = "application/x-review-section";

/** Max sections attachable per message (prompt size). */
export const MAX_ATTACHED_SECTIONS = 5;

export const ATTACHMENT_BLOCK_OPEN = "<<<review-sections";
export const ATTACHMENT_BLOCK_CLOSE = ">>>";

/**
 * Embed attached review rows at the start of the user message (persisted in DB).
 */
export function serializeAttachedSections(rows: ReviewRow[]): string {
  if (rows.length === 0) return "";
  return `${ATTACHMENT_BLOCK_OPEN}\n${JSON.stringify(rows)}\n${ATTACHMENT_BLOCK_CLOSE}\n\n`;
}

export interface ParsedAttachedSections {
  sections: ReviewRow[];
  cleanText: string;
}

/**
 * Strip the embedded attachment block and return structured rows + visible text.
 */
export function parseAttachedSections(text: string): ParsedAttachedSections {
  const idx = text.indexOf(ATTACHMENT_BLOCK_OPEN);
  if (idx === -1) {
    return { sections: [], cleanText: text };
  }

  const afterOpen = idx + ATTACHMENT_BLOCK_OPEN.length;
  if (text.charAt(afterOpen) !== "\n") {
    return { sections: [], cleanText: text };
  }

  const jsonStart = afterOpen + 1;
  const closeIdx = text.indexOf(ATTACHMENT_BLOCK_CLOSE, jsonStart);
  if (closeIdx === -1) {
    return { sections: [], cleanText: text };
  }

  const jsonStr = text.slice(jsonStart, closeIdx).trim();
  let sections: ReviewRow[] = [];
  try {
    const parsed = JSON.parse(jsonStr) as unknown;
    if (Array.isArray(parsed)) {
      sections = parsed as ReviewRow[];
    }
  } catch {
    sections = [];
  }

  const before = text.slice(0, idx).trimEnd();
  const afterClose = closeIdx + ATTACHMENT_BLOCK_CLOSE.length;
  let tail = text.slice(afterClose).replace(/^\s+/, "");
  const cleanText = [before, tail].filter(Boolean).join("\n\n").trim();

  return { sections, cleanText };
}

export function reviewRowDedupeKey(row: Pick<ReviewRow, "section" | "pageNumber">): string {
  return `${row.section}::${row.pageNumber}`;
}
